import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./CartContext";
import { useCountry } from "./CountryContext";
import "./Cart.css";

function Cart() {
  const navigate = useNavigate();

  const {
    cartItems,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    cartTotal,
  } = useCart();

  const { formatPrice } = useCountry();

  // =========================================
  // CUSTOM MEASUREMENTS POPUP
  // =========================================

  const [selectedCustomItem, setSelectedCustomItem] = useState(null);

  // =========================================
  // EMPTY CART
  // =========================================

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              🛍️
            </div>

            <h1>
              Your Cart Is Empty
            </h1>

            <p>
              You haven't added any products to your cart yet.
            </p>

            <button
              type="button"
              className="continue-shopping-btn"
              onClick={() => navigate("/view-all")}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">

        {/* =========================
            PAGE HEADING
        ========================== */}

        <div className="cart-heading">
          <p className="cart-small-title">
            FASHION AFRIQUE
          </p>

          <h1>
            Shopping Cart
          </h1>

          <div className="cart-heading-line"></div>
        </div>


        <div className="row g-4">

          {/* =========================
              CART PRODUCTS
          ========================== */}

          <div className="col-12 col-lg-8">

            <div className="cart-products-box">

              {cartItems.map((item) => (

                <div
                  className="cart-product-item"
                  key={
                    item.cartItemKey ||
                    `${item.id}-${item.selectedSize || "no-size"}`
                  }
                >

                  {/* =========================
                      PRODUCT IMAGE
                  ========================== */}

                  <div className="cart-product-image-box">

                    <img
                      src={item.image || item.img}
                      alt={item.name}
                      className="cart-product-image"
                    />

                  </div>


                  {/* =========================
                      PRODUCT INFORMATION
                  ========================== */}

                  <div className="cart-product-info">

                    <h2 className="cart-product-name">
                      {item.name}
                    </h2>


                    {/* =========================
                        SIZE
                    ========================== */}

                    {item.selectedSize ? (

                      <div className="cart-product-size">

                        <span>
                          Size:
                        </span>

                        <strong
                          className={
                            item.selectedSize === "Custom"
                              ? "custom-size-text"
                              : ""
                          }
                        >
                          {item.selectedSize === "Custom"
                            ? "Custom ✨"
                            : item.selectedSize}
                        </strong>

                      </div>

                    ) : (

                      <div className="cart-product-size">

                        <span>
                          Size:
                        </span>

                        <strong>
                          Not Selected
                        </strong>

                      </div>

                    )}


                    {/* =========================
                        CUSTOM MEASUREMENTS BUTTON
                    ========================== */}

                    {item.selectedSize === "Custom" &&
                      item.customMeasurements && (

                        <button
                          type="button"
                          className="view-custom-measurements-btn"
                          onClick={() =>
                            setSelectedCustomItem(item)
                          }
                        >
                          📏 View Custom Measurements
                        </button>

                    )}


                    {/* =========================
                        PRICE
                    ========================== */}

                    <div className="cart-product-price">

                      {formatPrice(item.price)}

                    </div>


                    {/* =========================
                        QUANTITY
                    ========================== */}

                    <div className="cart-quantity-row">

                      <span className="cart-quantity-label">
                        Quantity
                      </span>

                      <div className="cart-quantity-box">

                        {/* MINUS */}

                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={() =>
                            decreaseQuantity(
                              item.cartItemKey
                            )
                          }
                        >
                          −
                        </button>


                        {/* QUANTITY */}

                        <span className="cart-quantity-number">
                          {item.quantity}
                        </span>


                        {/* PLUS */}

                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={() =>
                            increaseQuantity(
                              item.cartItemKey
                            )
                          }
                        >
                          +
                        </button>

                      </div>

                    </div>


                    {/* =========================
                        ITEM TOTAL
                    ========================== */}

                    <div className="cart-item-total">

                      <span>
                        Item Total:
                      </span>

                      <strong>
                        {formatPrice(
                          Number(item.price) *
                            Number(item.quantity)
                        )}
                      </strong>

                    </div>


                    {/* =========================
                        REMOVE
                    ========================== */}

                    <button
                      type="button"
                      className="cart-remove-btn"
                      onClick={() =>
                        removeFromCart(
                          item.cartItemKey
                        )
                      }
                    >
                      Remove
                    </button>

                  </div>

                </div>

              ))}

            </div>

          </div>


          {/* =========================
              ORDER SUMMARY
          ========================== */}

          <div className="col-12 col-lg-4">

            <div className="cart-summary">

              <h2>
                Order Summary
              </h2>


              {/* PRODUCTS COUNT */}

              <div className="cart-summary-line">

                <span>
                  Products
                </span>

                <strong>
                  {cartItems.reduce(
                    (total, item) =>
                      total +
                      Number(item.quantity || 0),
                    0
                  )}
                </strong>

              </div>


              {/* SUBTOTAL */}

              <div className="cart-summary-line">

                <span>
                  Subtotal
                </span>

                <strong>
                  {formatPrice(cartTotal)}
                </strong>

              </div>


              {/* SHIPPING */}

              <div className="cart-summary-line">

                <span>
                  Shipping
                </span>

                <strong className="free-shipping">
                  FREE
                </strong>

              </div>


              <div className="cart-summary-divider"></div>


              {/* TOTAL */}

              <div className="cart-total-line">

                <span>
                  Total
                </span>

                <strong>
                  {formatPrice(cartTotal)}
                </strong>

              </div>


              {/* =========================
                  CHECKOUT
              ========================== */}

              <button
                type="button"
                className="cart-checkout-btn"
                onClick={() =>
                  navigate("/payment")
                }
              >
                Proceed to Checkout
              </button>


              {/* =========================
                  CONTINUE SHOPPING
              ========================== */}

              <button
                type="button"
                className="cart-continue-btn"
                onClick={() =>
                  navigate("/view-all")
                }
              >
                Continue Shopping
              </button>

            </div>

          </div>

        </div>

      </div>


      {/* =========================================
          CUSTOM MEASUREMENTS MODAL
      ========================================= */}

      {selectedCustomItem &&
        selectedCustomItem.customMeasurements && (

          <div
            className="custom-cart-modal-overlay"
            onClick={() =>
              setSelectedCustomItem(null)
            }
          >

            <div
              className="custom-cart-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* MODAL HEADER */}

              <div className="custom-cart-modal-header">

                <div>

                  <p>
                    CUSTOM SIZE
                  </p>

                  <h2>
                    Custom Measurements
                  </h2>

                </div>

                <button
                  type="button"
                  className="custom-cart-modal-close"
                  onClick={() =>
                    setSelectedCustomItem(null)
                  }
                >
                  ×
                </button>

              </div>


              {/* PRODUCT NAME */}

              <div className="custom-cart-product-name">

                <span>
                  Product
                </span>

                <strong>
                  {selectedCustomItem.name}
                </strong>

              </div>


              {/* MEASUREMENTS */}

              <div className="custom-cart-measurements-grid">

                <div>

                  <span>
                    Bust / Chest
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .bust
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Waist
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .waist
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Hip
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .hip
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Shoulder
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .shoulder
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Sleeve Length
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .sleeveLength
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Dress Length
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .dressLength
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Armhole
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .armhole
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Neck Size
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .neckSize
                    }{" "}
                    in
                  </strong>

                </div>


                <div>

                  <span>
                    Height
                  </span>

                  <strong>
                    {
                      selectedCustomItem
                        .customMeasurements
                        .height
                    }{" "}
                    in
                  </strong>

                </div>

              </div>


              {/* UNIT */}

              <div className="custom-cart-unit">

                Measurements are provided in{" "}

                <strong>
                  {
                    selectedCustomItem
                      .customMeasurements
                      .unit || "inches"
                  }
                </strong>

                .

              </div>


              {/* CLOSE */}

              <button
                type="button"
                className="custom-cart-modal-done"
                onClick={() =>
                  setSelectedCustomItem(null)
                }
              >
                Close
              </button>

            </div>

          </div>

        )}

    </div>
  );
}

export default Cart;

